import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, CircleDollarSign } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function FestivalPricing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState("");

  const load = async () => {
    const { data } = await api.get("/festival/pricing");
    setPricing(data.pricing);
  };
  useEffect(() => { load(); }, []);

  const save = async (key, def) => {
    setSaving(key);
    try {
      await api.put("/festival/pricing", {
        pass_type: key,
        amount: Number(def.amount),
        currency: (def.currency || "gbp").toLowerCase(),
        label: def.label,
        days: Number(def.days) || 1,
      });
      toast.success("Pricing updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving("");
    }
  };

  const setField = (key, field, val) => {
    setPricing((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), [field]: val } }));
  };

  if (!pricing) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-pricing">
      <button
        type="button"
        onClick={() => navigate("/festival/mine")}
        className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Festivals
      </button>
      <div className="label-mono text-[#CCFF00] mb-1">RALLY-LAB OWNER · PRICING</div>
      <h1 className="h-display text-3xl mb-6 flex items-center gap-2">
        <CircleDollarSign className="w-7 h-7 text-[#CCFF00]" /> Event pass pricing
      </h1>

      <div className="space-y-4">
        {Object.entries(pricing).map(([key, def]) => (
          <div key={key} className="card-surface p-5" data-testid={`pricing-row-${key}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#525252]">{key}</div>
                <input
                  value={def.label || ""}
                  onChange={(e) => setField(key, "label", e.target.value)}
                  data-testid={`pricing-label-${key}`}
                  className="text-lg font-semibold bg-transparent border-b border-transparent focus:border-[#CCFF00] focus:outline-none"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="label-mono text-[#A3A3A3] block mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={def.amount ?? 0}
                  onChange={(e) => setField(key, "amount", e.target.value)}
                  data-testid={`pricing-amount-${key}`}
                  className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
                />
              </div>
              <div>
                <label className="label-mono text-[#A3A3A3] block mb-1">Currency</label>
                <input
                  value={def.currency ?? "gbp"}
                  onChange={(e) => setField(key, "currency", e.target.value)}
                  data-testid={`pricing-currency-${key}`}
                  maxLength={3}
                  className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00] uppercase"
                />
              </div>
              <div>
                <label className="label-mono text-[#A3A3A3] block mb-1">Days</label>
                <input
                  type="number"
                  min={1}
                  max={14}
                  value={def.days ?? 1}
                  onChange={(e) => setField(key, "days", e.target.value)}
                  data-testid={`pricing-days-${key}`}
                  className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
                />
              </div>
            </div>
            <div className="pt-3 mt-3 border-t border-[#262626] flex justify-end">
              <button
                type="button"
                onClick={() => save(key, def)}
                disabled={!!saving}
                data-testid={`pricing-save-${key}`}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-[11px] uppercase tracking-wider font-bold disabled:opacity-50"
                style={{ background: "#CCFF00", color: "#0A0A0A" }}
              >
                <Save className="w-3.5 h-3.5" />
                {saving === key ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
